import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getStageTheme } from './stageColorTheme';

type RoleTargetStatus = 'achieved' | 'healthy' | 'watch' | 'at_risk' | 'no_target';

interface RoleTargetRow {
  roleId: string;
  role: string;
  target: number;
  selected: number;
  accepted: number;
  joined: number;
  selectedRatio: number | null;
  acceptedRatio: number | null;
  joinedRatio: number | null;
  gapToTarget: number;
  status: RoleTargetStatus;
}

interface RoleTargetTrackerProps {
  data: RoleTargetRow[];
}

const statusLabel: Record<RoleTargetStatus, string> = {
  achieved: 'Target Met',
  healthy: 'Pipeline Healthy',
  watch: 'Needs Attention',
  at_risk: 'Off Track',
  no_target: 'No Target',
};

const statusVariant: Record<RoleTargetStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  achieved: 'default',
  healthy: 'secondary',
  watch: 'outline',
  at_risk: 'destructive',
  no_target: 'outline',
};

const formatRatio = (value: number | null) => (value === null ? '-' : `${value.toFixed(1)}x`);

const formatGap = (value: number) => {
  if (value > 0) return `+${value}`;
  return `${value}`;
};

function HeaderWithTooltip({ label, description }: { label: string; description: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-left">
          {description}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function RoleTargetTracker({ data }: RoleTargetTrackerProps) {
  const selectedTheme = getStageTheme('Selected');
  const acceptedTheme = getStageTheme('Accepted');
  const joinedTheme = getStageTheme('Joined');

  const achievedRoles = data.filter((row) => row.status === 'achieved').length;
  const healthyBufferRoles = data.filter(
    (row) => row.selectedRatio !== null && row.acceptedRatio !== null && row.selectedRatio >= 3 && row.acceptedRatio >= 2
  ).length;
  const atRiskRoles = data.filter((row) => row.status === 'at_risk').length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Role Hiring Targets</CardTitle>
        <CardDescription>
          Compare target joins with current selected, accepted, and joined counts for each role
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
          Target means planned final joins for the role. Selected and accepted show pipeline strength, joined shows actual hires completed, and gap shows how many more joiners are still needed to hit the target.
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="text-sm text-muted-foreground">Roles already meeting join target</div>
            <div className="mt-1 text-2xl font-semibold">{achievedRoles}</div>
          </div>
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="text-sm text-muted-foreground">Roles with enough selection buffer</div>
            <div className="mt-1 text-2xl font-semibold">{healthyBufferRoles}</div>
            <div className="mt-1 text-xs text-muted-foreground">3x selected and 2x accepted against target</div>
          </div>
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="text-sm text-muted-foreground">Roles likely to miss target</div>
            <div className="mt-1 text-2xl font-semibold">{atRiskRoles}</div>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label="Target Joins"
                  description="Planned final hires for this role in the selected hiring year."
                />
              </TableHead>
              <TableHead className={`text-right ${selectedTheme.text}`}>
                <HeaderWithTooltip
                  label="Selected"
                  description="Candidates who reached selected status for this role in the selected year."
                />
              </TableHead>
              <TableHead className={`text-right ${acceptedTheme.text}`}>
                <HeaderWithTooltip
                  label="Accepted"
                  description="Candidates who accepted the offer for this role."
                />
              </TableHead>
              <TableHead className={`text-right ${joinedTheme.text}`}>
                <HeaderWithTooltip
                  label="Joined"
                  description="Candidates who actually joined for this role. This is the final achieved hiring count."
                />
              </TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label="Selection Buffer"
                  description="Selected divided by target joins. Example: 2.0x means selected candidates are twice the target."
                />
              </TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label="Acceptance Buffer"
                  description="Accepted divided by target joins. This shows how strong the confirmed pipeline is against the target."
                />
              </TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label="Join Progress"
                  description="Joined divided by target joins. Example: 0.5x means half the final hiring target has been achieved."
                />
              </TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label="Join Gap"
                  description="Joined minus target joins. Negative means you still need more hires to meet the target."
                />
              </TableHead>
              <TableHead className="text-right">
                <HeaderWithTooltip
                  label="Status"
                  description="Overall hiring health for the role based on target achievement and pipeline buffer strength."
                />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground">
                  No role target data available
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.roleId}>
                  <TableCell className="font-medium">{row.role}</TableCell>
                  <TableCell className="text-right">{row.target}</TableCell>
                  <TableCell className="text-right">
                    <span className={`inline-flex min-w-9 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold ${selectedTheme.tint}`}>
                      {row.selected}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`inline-flex min-w-9 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold ${acceptedTheme.tint}`}>
                      {row.accepted}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`inline-flex min-w-9 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold ${joinedTheme.tint}`}>
                      {row.joined}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">{formatRatio(row.selectedRatio)}</TableCell>
                  <TableCell className="text-right">{formatRatio(row.acceptedRatio)}</TableCell>
                  <TableCell className="text-right">{formatRatio(row.joinedRatio)}</TableCell>
                  <TableCell className="text-right">{formatGap(row.gapToTarget)}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={statusVariant[row.status]}>{statusLabel[row.status]}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
